import { Component } from '@theme/component';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The "Early Access" signup for products tagged `coming-soon`. The form itself
 * is a native {% form 'customer' %} (a newsletter signup: the shopper becomes a
 * Customer with email marketing consent, tagged `coming-soon-lead`) that
 * submits normally - full page request, success/error state rendered
 * server-side - since Shopify's spam protection rejects AJAX posts there.
 *
 * This component only gates that native submit with a client-side
 * email/honeypot check, calling `preventDefault()` when it fails.
 *
 * @typedef {object} ComingSoonSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [honeypotInput]
 * @extends {Component<ComingSoonSignupRefs>}
 */
class ComingSoonSignupComponent extends Component {
  requiredRefs = ['form'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.form.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const { emailInput, honeypotInput } = this.refs;

    if (honeypotInput instanceof HTMLInputElement && honeypotInput.value !== '') {
      // Likely a bot: silently drop the submission, no error feedback either way.
      event.preventDefault();
      return;
    }

    if (emailInput instanceof HTMLInputElement && !EMAIL_REGEX.test(emailInput.value.trim())) {
      event.preventDefault();
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.reportValidity?.();
      emailInput.focus();
    }
  };
}

if (!customElements.get('coming-soon-signup-component')) {
  customElements.define('coming-soon-signup-component', ComingSoonSignupComponent);
}
